import React, { useState, useEffect, useMemo } from "react";
import { UnitMissionCapabilityForUnitAndMissionDTO } from '../types/unitMissionCapabilityForUnitAndMissionDTO';
import { useFetchCapabilities } from "../hooks/CapabilityHooks";
import { useFetchCodeSystem } from "../hooks/CodeHooks";
import { useFetchUserGroups } from "../hooks/UserGroupHooks";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchUnits } from "../hooks/UnitHooks";
import { CapabilityDTO } from "../types/capabilityDTO";
import { UserGroupDTO } from "../types/userGroupDTO";
import { UnitMissionCapabilityDTO } from "../types/unitMissionCapabilityDTO";
import { useTranslation } from 'react-i18next';
import { CodeDTO } from "../types/codeDTO";

interface UnitMissionCapabilityFormArgs {
    headerText: string;
    isEdit: boolean;
    unitMissionCapabilityForUnitAndMission: UnitMissionCapabilityForUnitAndMissionDTO;
    submitted: (capability: UnitMissionCapabilityForUnitAndMissionDTO[]) => void;
    deleted: (capability: UnitMissionCapabilityForUnitAndMissionDTO) => void;
}

const UnitMissionCapabilityForm = ({ headerText, isEdit, unitMissionCapabilityForUnitAndMission: initialData, submitted, deleted }: UnitMissionCapabilityFormArgs) => {
    const defaultMissionCodeValue = '00000000-0000-0000-0000-000000000000';
    const initialMissionCodeId = initialData.missionCode ? initialData.missionCode.id : defaultMissionCodeValue;
    const initialUnitId = initialData.unit?.id;
    const { t } = useTranslation();
    const nav = useNavigate();
    const { organisationId } = useParams<{ organisationId: string }>();
    if (!organisationId) throw new Error("Missing organisationId from params");
    const [unitMissionCapabilityState, setUnitMissionCapabilityState] = useState<UnitMissionCapabilityForUnitAndMissionDTO>(initialData);
    const { data: availableCapabilities, isPending: capabilititieIsPending, isError: capabilitiesIsError } = useFetchCapabilities(organisationId);
    const { data: availableMissionCodes, isPending: missionCodesIsPending, isError: missionCodesIsError } = useFetchCodeSystem(organisationId, "Tehtäväkoodi");
    const { data: availableUnits, isPending: unitsIsPending, isError: unitsIsError } = useFetchUnits(organisationId);
    const { data: availableUserGroups, isPending: userGroupsIsPending, isError: userGroupsIsError } = useFetchUserGroups(organisationId);

    const [selectedMissionCodes, setSelectedMissionCodes] = useState<string[]>(initialData.missionCode ? [initialData.missionCode.id] : [defaultMissionCodeValue]);
    const [selectedUnits, setSelectedUnits] = useState<string[]>(initialData.unit ? [initialData.unit.id] : []);
    const [missionCodeFilter, setMissionCodeFilter] = useState<string>('');

    useEffect(() => {
        setUnitMissionCapabilityState(initialData);
        setSelectedMissionCodes(initialData.missionCode ? [initialData.missionCode.id] : [defaultMissionCodeValue]);
        setSelectedUnits(initialData.unit ? [initialData.unit.id] : []);


    }, [initialData]);

    const sortedMissionCodes = useMemo(
        () => availableMissionCodes ? [...availableMissionCodes.codes].sort((a, b) => a.codedValue.localeCompare(b.codedValue)) : [],
        [availableMissionCodes]
    );

    const sortedUnits = useMemo(
        () => availableUnits ? [...availableUnits].sort((a, b) => a.name.localeCompare(b.name)) : [],
        [availableUnits]
    );

    const visibleMissionCodes = useMemo(() => {
        const filter = missionCodeFilter.trim().toLowerCase();
        if (!filter) return sortedMissionCodes;

        return sortedMissionCodes.filter(missionCode =>
            selectedMissionCodes.indexOf(missionCode.id) >= 0
            || missionCode.codedValue.toLowerCase().indexOf(filter) >= 0
            || missionCode.value.toLowerCase().indexOf(filter) >= 0
        );
    }, [sortedMissionCodes, missionCodeFilter, selectedMissionCodes]);

    if (capabilitiesIsError) return <h2>Error while loading available capabilities</h2>;
    if (missionCodesIsError) return <h2>Error while loading available mission codes</h2>;
    if (unitsIsError) return <h2>Error while loading available units</h2>;
    if (userGroupsIsError) return <h2>Error while loading user groups</h2>;
    if (capabilititieIsPending || missionCodesIsPending || unitsIsPending || userGroupsIsPending) return <h2>Hold on</h2>;
    if (!availableMissionCodes) return <h2>No mission codes found</h2>;
    if (!availableUnits) return <h2>No units found</h2>;

    const handleCapabilityChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;
        const capability: CapabilityDTO = availableCapabilities.find(c => c.id === value)!;
        const unitMissionCapability: UnitMissionCapabilityDTO = { id: "00000000-0000-0000-0000-000000000000", name: capability.name, capabilityId: capability.id, count: 1 };

        if (checked) {
            setUnitMissionCapabilityState(prevState => ({
                ...prevState,
                desiredCapabilities: [...prevState.desiredCapabilities, unitMissionCapability]
            }));
        } else {
            setUnitMissionCapabilityState(prevState => ({
                ...prevState,
                desiredCapabilities: prevState.desiredCapabilities.filter(c => c.capabilityId !== value)
            }));
        }
    };

    const handleGroupChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;

        if (value === "all") {
            if (checked) {
                setUnitMissionCapabilityState(prevState => ({ ...prevState, requiredGroups: [] }));
            }
            else {
                setUnitMissionCapabilityState(prevState => ({ ...prevState, requiredGroups: availableUserGroups.map(g => ({ id: "00000000-0000-0000-0000-000000000000", name: g.name, userGroupId: g.id })) }));
            }

        }
        else {

            const userGroup: UserGroupDTO = availableUserGroups.find(g => g.id === value)!;
            const requiredGroup = { id: "00000000-0000-0000-0000-000000000000", name: userGroup.name, userGroupId: userGroup.id };

            if (checked) {
                setUnitMissionCapabilityState(prevState => ({
                    ...prevState,
                    requiredGroups: [...prevState.requiredGroups, requiredGroup]
                }));

            }
            else {
                setUnitMissionCapabilityState(prevState => ({
                    ...prevState,
                    requiredGroups: prevState.requiredGroups.filter(g => g.userGroupId !== value)
                }));

            }


        }


    };


    const handleCapabilityCountChange = async (event: React.ChangeEvent<HTMLInputElement>, capabilityId: string) => {
        const { value } = event.target;
        const count = parseInt(value, 10) || 0;

        setUnitMissionCapabilityState(prevState => ({
            ...prevState,
            desiredCapabilities: prevState.desiredCapabilities.map(c =>
                c.capabilityId === capabilityId ? { ...c, count } : c
            )
        }));

        if (count === 0) {
            setUnitMissionCapabilityState(prevState => ({
                ...prevState,
                desiredCapabilities: prevState.desiredCapabilities.filter(c => c.capabilityId !== capabilityId)
            }));
        }
    };

    const handleMissionCodeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;
        if (isEdit && !checked && value === initialMissionCodeId) {
            return;
        }

        const nextSelectedMissionCodes = checked
            ? (selectedMissionCodes.indexOf(value) >= 0 ? selectedMissionCodes : [...selectedMissionCodes, value])
            : selectedMissionCodes.filter(id => id !== value);

        setSelectedMissionCodes(nextSelectedMissionCodes);
    };

    const handleUnitChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;
        if (isEdit && !checked && value === initialUnitId) {
            return;
        }

        const nextSelectedUnits = checked
            ? (selectedUnits.indexOf(value) >= 0 ? selectedUnits : [...selectedUnits, value])
            : selectedUnits.filter(id => id !== value);

        setSelectedUnits(nextSelectedUnits);
    };

    const onSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        const capabilitiesToSubmit: UnitMissionCapabilityForUnitAndMissionDTO[] = [];

        selectedUnits.forEach(unitId => {
            const unit = availableUnits.find(u => u.id === unitId);
            if (!unit) return;
       
            selectedMissionCodes.forEach(missionCodeId => {
                let missionCode: CodeDTO | null | undefined;

                if (missionCodeId === defaultMissionCodeValue)
                {
                    missionCode = null;                  
                }
                else
                {
                    missionCode = availableMissionCodes.codes.find(c => c.id === missionCodeId);
                    if (!missionCode) return;
                }
              
                capabilitiesToSubmit.push({
                    ...unitMissionCapabilityState,
                    unit,
                    missionCode,
                    desiredCapabilities: unitMissionCapabilityState.desiredCapabilities.map(c => ({ ...c })),
                    requiredGroups: unitMissionCapabilityState.requiredGroups.map(g => ({ ...g }))
                });
            });
        });

        submitted(capabilitiesToSubmit);
    };

    const onCancel = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        nav(-1);
    };

    const onDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        deleted(unitMissionCapabilityState);
    }

    const missionCodeInput = (
        <div className="form-group">
            <label htmlFor="missionCode">{t('common.missionCode')}</label>
            <input
                id="missionCodeFilter"
                type="text"
                className="form-control mt-2"
                value={missionCodeFilter}
                onChange={(e) => setMissionCodeFilter(e.target.value)}
                placeholder={t('unitMissionCapability.filterMissionCodes')}
            />
            <div
                id="missionCode"
                className="mt-2"
                style={{
                    maxHeight: '220px',
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.375rem',
                    padding: '0.5rem'
                }}
            >
                <div className="form-check">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id="missionCode-default"
                        value={defaultMissionCodeValue}
                        disabled={isEdit && initialMissionCodeId === defaultMissionCodeValue}
                        checked={selectedMissionCodes.indexOf(defaultMissionCodeValue) >= 0}
                        onChange={handleMissionCodeChange}
                    />
                    <label className="form-check-label" htmlFor="missionCode-default">
                        {isEdit && initialMissionCodeId === defaultMissionCodeValue ? <strong>{t('common.default')}</strong> : t('common.default')}
                    </label>
                </div>
                {visibleMissionCodes.map((missionCode) => (
                    <div key={missionCode.id} className="form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={`missionCode-${missionCode.id}`}
                            value={missionCode.id}
                            disabled={isEdit && missionCode.id === initialMissionCodeId}
                            checked={selectedMissionCodes.indexOf(missionCode.id) >= 0}
                            onChange={handleMissionCodeChange}
                        />
                        <label className="form-check-label" htmlFor={`missionCode-${missionCode.id}`}>
                            {isEdit && missionCode.id === initialMissionCodeId
                                ? <strong>{missionCode.codedValue} {missionCode.value}</strong>
                                : <>{missionCode.codedValue} {missionCode.value}</>}
                        </label>
                    </div>
                ))}
            </div>
        </div>
    );

    const unitInput = (
        <div className="form-group">
            <label htmlFor="unit">{t('common.unit')}</label>
            <div
                id="unit"
                className="mt-2"
                style={{
                    maxHeight: '220px',
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.375rem',
                    padding: '0.5rem'
                }}
            >
                {sortedUnits.map((unit) => (
                    <div key={unit.id} className="form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={`unit-${unit.id}`}
                            value={unit.id}
                            disabled={isEdit && unit.id === initialUnitId}
                            checked={selectedUnits.indexOf(unit.id) >= 0}
                            onChange={handleUnitChange}
                        />
                        <label className="form-check-label" htmlFor={`unit-${unit.id}`}>
                            {isEdit && unit.id === initialUnitId ? <strong>{unit.name}</strong> : unit.name}
                        </label>
                    </div>
                ))}
            </div>
        </div>
    );

    const UserGroupRow = ({ userGroup }: { userGroup: UserGroupDTO }) => (
        <tr key={userGroup.id}>
            <td>
                <input
                    type="checkbox"
                    className="form-check-input"
                    id={userGroup.id}
                    value={userGroup.id}
                    checked={unitMissionCapabilityState.requiredGroups.some(g => g.userGroupId === userGroup.id)}
                    onChange={handleGroupChange}
                />
            </td>
            <td>
                <label className="form-check-label" htmlFor={userGroup.id}>
                    {userGroup.name}
                </label>
            </td>
        </tr>
    );      

    return (
        <form>
            <div>
                <h5>{headerText}</h5>
            </div>
            {missionCodeInput}
            {unitInput}
            <div className="form-group">
                <label htmlFor="address">{t('unitMissionCapability.desiredCapabilities')}</label>
                <div className="row g-2 mt-1">
                    {availableCapabilities.map((capability) => (
                        <div key={capability.id} className="col-3">
                            <div className="border rounded py-1 px-2">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={capability.id}
                                        value={capability.id}
                                        checked={unitMissionCapabilityState.desiredCapabilities.some(c => c.capabilityId === capability.id)}
                                        onChange={handleCapabilityChange}
                                    />
                                    <label className="form-check-label" htmlFor={capability.id}>
                                        {capability.name}
                                    </label>
                                </div>
                                <input
                                    type="number"
                                    className="form-control mt-1"
                                    min="0"
                                    value={unitMissionCapabilityState.desiredCapabilities.find(c => c.capabilityId === capability.id)?.count || 0}
                                    onChange={(e) => handleCapabilityCountChange(e, capability.id)}
                                    disabled={!unitMissionCapabilityState.desiredCapabilities.some(c => c.capabilityId === capability.id)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="form-group">
                <label htmlFor="address">{t('unitMissionCapability.inviteGroups')}</label>
                <table className="mt-2">
                    <tbody>
                        <tr key={"allGroups"}>
                            <td>
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={"allGroups"}
                                    value={"all"}
                                    checked={unitMissionCapabilityState.requiredGroups.length === 0}
                                    onChange={handleGroupChange}
                                />
                            </td>
                            <td>
                                <label className="form-check-label" htmlFor={"allGroups"}>
                                    {t('unitMissionCapability.all')}
                                </label>
                            </td>
                        </tr>
                        {availableUserGroups.map((group) => (
                            <UserGroupRow key={group.id} userGroup={group} />
                        ))}
                    </tbody>
                </table>
            </div>
            <button
                className="btn btn-primary mt-3 me-2"
                disabled={!unitMissionCapabilityState.desiredCapabilities.length || !selectedUnits.length || !selectedMissionCodes.length}
                onClick={onSubmit}
            >
                {t('buttons.save')}
            </button>
            <button
                className="btn btn-danger mt-3 me-2"
                disabled={!isEdit}
                onClick={onDelete}>
                {t('buttons.delete')}
            </button>
            <button
                className="btn btn-danger mt-3 me-2"
                onClick={onCancel}>
                {t('buttons.cancel')}
            </button>
        </form>
    );
};

export default UnitMissionCapabilityForm;